import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { StatementService } from '../services/statement.service';
import { EventService } from '../services/event.service';
import { LoggedUserService } from '../services/logged-user.service';
import { AttentionModel } from '../models/shared/attention.model';
import { LoadingModel } from '../models/shared/loading.model';
import { EventName, UserRoleEnum } from '../shared/enums';
import { SUBSCRIPTION_MESSAGE_FOR_ADMIN, SUBSCRIPTION_MESSAGE_FOR_MEMBER, LAYOUT_PART } from '../shared/constants';
import { Observable, of } from 'rxjs';
import { map, catchError, finalize } from 'rxjs/operators';

function showSubscriptionError(
  eventService: EventService,
  loggedUserService: LoggedUserService
): void {
  const loggedUser = loggedUserService.loggedUser;
  const isAdmin = loggedUser?.user_role === UserRoleEnum.Admin;

  const message = isAdmin
    ? SUBSCRIPTION_MESSAGE_FOR_ADMIN
    : SUBSCRIPTION_MESSAGE_FOR_MEMBER;

  const buttonText = isAdmin ? 'Go to Subscription' : 'Close';

  const attentionData = new AttentionModel({
    visible: true,
    message: message,
    buttonText: buttonText,
    blocking: true,
    isAlert: true,
    action: 'SUBSCRIPTION_VALIDATION'
  });

  eventService.broadcast(EventName.ShowAttention, attentionData);
}

export const SubscriptionValidationGuard: CanActivateFn = (): Observable<boolean> => {
  const statementService = inject(StatementService);
  const eventService = inject(EventService);
  const loggedUserService = inject(LoggedUserService);

  // Show loading
  const loadingData = new LoadingModel({
    visible: true,
    shading: true,
    position: { of: LAYOUT_PART.body }
  });
  eventService.broadcast(EventName.ShowLoading, loadingData);

  return statementService.validateUsage().pipe(
    map((response) => {
      // On success (200 OK), allow navigation
      if (!response.isError) {
        return true;
      }
      // If API returns error in response object
      if (response.status === 402) {
        showSubscriptionError(eventService, loggedUserService);
        return false;
      }
      return true;
    }),
    catchError((error) => {
      // Handle 402 Payment Required error
      if (error.status === 402 && error.statusText === 'Payment Required') {
        showSubscriptionError(eventService, loggedUserService);
        return of(false);
      }
      // For other errors, allow navigation (fail open)
      return of(true);
    }),
    finalize(() => {
      // Hide loading
      loadingData.visible = false;
      eventService.broadcast(EventName.ShowLoading, loadingData);
    })
  );
};
